import { formatCurrency } from './formatters';

/**
 * Cost per user (#17): the API computes seatCount and costPerUser (annual value over the seats
 * of the contract's active licenses, else its active allocations, else null). This module holds
 * the display rule for a missing value and the per-license data-quality hint shown on the
 * contract page.
 */

/** Shown wherever a contract has no seat data; never zero, never infinity */
export const NOT_AVAILABLE = 'n/a';

/** Fraction of the annual value a license's quantity x cost per unit may differ by before it is flagged */
export const COST_MISMATCH_TOLERANCE = 0.1;

const twoDecimals = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

/** "$120.00", or "n/a" when the contract has no seat data */
export function formatCostPerUser(value: number | null | undefined): string {
  return value === null || value === undefined ? NOT_AVAILABLE : formatCurrency(value, twoDecimals);
}

/** The seat count as text, or "n/a" when the contract has no seat data */
export function formatSeatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? NOT_AVAILABLE : String(value);
}

/** What a license costs for a year at its recorded cost per unit: quantity x cost per unit */
export function licenseAnnualCost(license: { costPerUnit: number; totalQuantity: number }): number {
  return license.costPerUnit * license.totalQuantity;
}

/**
 * True when the license's quantity x cost per unit is more than 10 percent off the contract's
 * annual value. A data-quality hint, not an error: a contract with several licenses flags each one
 * against the whole annual value. Nothing is flagged without a cost per unit or an annual value,
 * and exactly 10 percent off is within tolerance.
 */
export function isLicenseCostMismatch(
  license: { costPerUnit: number; totalQuantity: number },
  annualContractValue: number,
): boolean {
  if (license.costPerUnit <= 0 || annualContractValue <= 0) return false;
  // Compared in whole cents: 10 x 99.99 against 1,111 is exactly 10 percent off, but in floating
  // point the ratio lands a hair above 0.1 and would be flagged
  const costCents = Math.round(licenseAnnualCost(license) * 100);
  const annualCents = Math.round(annualContractValue * 100);
  return Math.abs(costCents - annualCents) > annualCents * COST_MISMATCH_TOLERANCE;
}

/** The hint's text, naming both figures and the percentage difference */
export function describeLicenseCostMismatch(
  license: { costPerUnit: number; totalQuantity: number },
  annualContractValue: number,
): string {
  const cost = licenseAnnualCost(license);
  const percent = Math.round((Math.abs(cost - annualContractValue) / annualContractValue) * 100);
  return `Quantity x cost per unit is ${formatCurrency(cost, twoDecimals)}, ${percent}% off the contract's annual value of ${formatCurrency(annualContractValue, twoDecimals)}`;
}
